import { ExamStatus, Group, Page } from '$synapse/models';
import { Component, OnChanges, inject, input } from '@angular/core';
import { CandidateService, GroupService } from '$synapse/services';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmModalComponent, SynDialogConfig } from '$synapse/core';
import { CandidatesAddToGroupComponent } from '../../../modules/candidates/candidates-add-to-group/candidates-add-to-group.component';
import { CandidateSummary } from '$synapse/dtos';
import { ToastrService } from 'ngx-toastr';
import { ExamSummary } from '../../../dtos/exam-summary';
import { MatDialog } from '@angular/material/dialog';
import { SHARED_IMPORTS } from '../../shared-imports';

type GroupedTests = {
  label: string;
  status: ExamStatus;
  duplicates: ExamSummary[];
};

@Component({
  selector: 'syn-group-candidates-table',
  templateUrl: './group-candidates-table.component.html',
  styleUrls: ['./group-candidates-table.component.scss'],
  imports: [SHARED_IMPORTS],
})
export class GroupCandidatesTableComponent implements OnChanges {
  private readonly _groupService = inject(GroupService);
  private readonly _candidateService = inject(CandidateService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _modalDialog = inject(MatDialog);
  private readonly _toastr = inject(ToastrService);

  readonly page = input<Page<CandidateSummary>>(undefined);
  readonly isSelectionTable = input(false);
  readonly isGroupDetails = input(true);
  readonly canDeleteCandidate = input(false);
  readonly displayAssessment = input(true);
  readonly group = input<Group>(undefined);

  selectedCandidates = new Array<CandidateSummary>();
  visible: boolean = false;
  allIsSelected: boolean;
  groupedTest: GroupedTests[][] = [];
  filteredCandidates: CandidateSummary[] = [];

  ngOnChanges() {
    this.filteredCandidates = [...this.page().content];
    const page = this.page();
    if (page?.content) {
      this.groupedTest = page.content.map((candidate) =>
        this.groupedTests(candidate.examSummaries)
      );
    }
  }

  filterByAdvancement(advancement: string) {
    if (!advancement) {
      this.filteredCandidates = [...this.page().content];
    } else {
      this.filteredCandidates = this.page().content.filter(
        (candidate) => candidate.advancement === advancement
      );
    }
  }

  masterToggle() {
    this.allIsSelected = this._isAllSelected();
    if (this._isAllSelected()) {
      this.emptySelection();
    } else {
      this.emptySelection();
      this.page().content.forEach((row) => {
        this._selectCandidate(row);
      });
    }
    this.visible = this.selectedCandidates.length !== 0;
  }

  private _isAllSelected() {
    const numSelected = this.selectedCandidates.length;
    const numRows = this.page().content.length;
    return numSelected === numRows;
  }

  boxChecked(row: CandidateSummary) {
    this._selectCandidate(row);
    this.visible = this.selectedCandidates.length !== 0;
  }

  emptySelection() {
    this.selectedCandidates = new Array<CandidateSummary>();
    this.visible = false;
  }

  ungroupCandidate() {
    const dialogRef = this._modalDialog.open(
      ConfirmModalComponent,
      new SynDialogConfig('ungroup-candidate-confirmation')
    );
    const instance = dialogRef.componentInstance;
    instance.title = 'Dissocier candidats';
    instance.message = 'Êtes-vous sûr de vouloir dissocier ce(s) candidat(s) ?';
    instance.action = 'Dissocier';
    dialogRef.componentInstance.confirmationEvent.subscribe(() => {
      this._groupService
        .removeCandidatesFromGroup(this.group(), this._getCandidatesIds())
        .subscribe((isSuccess) => {
          if (isSuccess) {
            this.emptySelection();
            this._router.navigate(
              ['/groups/' + this._route.snapshot.params['id']],
              {
                queryParamsHandling: 'merge',
              }
            );
          }
        });
    });
  }

  addCandidatesToGroup() {
    const dialogRef = this._modalDialog.open(
      CandidatesAddToGroupComponent,
      new SynDialogConfig('group-candidate', this.selectedCandidates)
    );

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        this.emptySelection();
        this.allIsSelected = false;
      }
    });
  }

  deleteCandidate() {
    const dialogRef = this._modalDialog.open(
      ConfirmModalComponent,
      new SynDialogConfig('delete-candidate-confirmation')
    );
    const instance = dialogRef.componentInstance;
    instance.title = 'Suppression de candidat(s)';
    instance.message = 'Êtes-vous sûr de vouloir supprimer ce(s) candidat(s) ?';
    instance.action = 'Supprimer';
    instance.warn = 'Attention ! Toutes les évaluations seront supprimées';
    dialogRef.componentInstance.confirmationEvent.subscribe(() => {
      this._candidateService
        .removeCandidates(this._getCandidatesIds())
        .subscribe((isSuccess) => {
          if (isSuccess) {
            this.emptySelection();
            this._router.navigate([location.pathname], {
              queryParamsHandling: 'merge',
            });
          }
        });
    });
  }

  sendEmailRemindCandidates() {
    if (
      this.selectedCandidates.filter(
        (candidate) => candidate.advancement === 'Terminé'
      ).length > 0
    ) {
      this._toastr.error(
        'Le ou les candidat(s) sélectionné(s) ont déjà terminé tous leurs questionnaires.',
        'Une erreur est survenue'
      );
    } else {
      const dialogRef = this._modalDialog.open(
        ConfirmModalComponent,
        new SynDialogConfig('send-email-confirmation')
      );
      let warn = `Attention ! Vous vous apprêtez à envoyer un e-mail à ${this.selectedCandidates.length} candidat(s). `;

      const instance = dialogRef.componentInstance;
      instance.title = `Envoi d'email de relance aux candidats`;
      instance.message = 'Êtes-vous sûr de vouloir envoyer ces mails ?';
      instance.action = 'Envoyer';
      instance.warn = warn;
      instance.confirmationEvent.subscribe(() => {
        this._groupService
          .sendEmailRemindCandidatesOfGroup(
            this.group().id,
            this.selectedCandidates.map((candidate) => candidate.id)
          )
          .subscribe();
      });
    }
  }

  private _selectCandidate(candidate: CandidateSummary) {
    if (this.selectedCandidates.includes(candidate)) {
      this._deleteInArray(candidate);
    } else {
      this.selectedCandidates.push(candidate);
    }

    this.allIsSelected = this._isAllSelected();
  }

  private _deleteInArray(key) {
    const index = this.selectedCandidates.indexOf(key);
    if (index > -1) {
      this.selectedCandidates.splice(index, 1);
    }
  }

  private _getCandidatesIds(): string[] {
    let candidatesIds = [];
    this.selectedCandidates.forEach((candidate) => {
      candidatesIds.push(candidate.id);
    });
    return candidatesIds;
  }

  groupedTests(test: ExamSummary[]): GroupedTests[] {
    const groups: { [label: string]: ExamSummary[] } = {};
    const examStatusList: ExamStatus[] = Object.values(ExamStatus);
    if (Array.isArray(test)) {
      test.forEach((t) => {
        if (!groups[t.label]) {
          groups[t.label] = [];
        }
        groups[t.label].push(t);
      });
    }

    return Object.entries(groups).map(([label, duplicates]) => ({
      label,
      duplicates,
      status:
        examStatusList.find((s) =>
          duplicates.some((test) => test.examStatus === s)
        ) ?? ExamStatus.UNREAD,
    }));
  }

  candidateAdvancementToolTipText(candidate: CandidateSummary) {
    switch (candidate.advancement) {
      case 'Non commencé':
        return `${candidate.firstName} ${candidate.lastName} n'a commencé aucun questionnaire`;
      case 'En cours':
        return `${candidate.firstName} ${candidate.lastName} a commencé à répondre aux questionnaires`;
      case 'Terminé':
        return `${candidate.firstName} ${candidate.lastName} a terminé tous ses questionnaires et les a réussis`;
    }
  }
}
